import React, { useEffect } from 'react';

/**
 * PerformanceOptimizer - компонент для оптимизации производительности сайта
 * - Управляет предзагрузкой критических ресурсов
 * - Оптимизирует загрузку шрифтов
 * - Внедряет стратегии отложенной загрузки для некритичных элементов
 */
const PerformanceOptimizer = () => {
  useEffect(() => {
    // Отложить загрузку некритичных ресурсов
    const deferNonCriticalResources = () => {
      // Добавление rel=preconnect для основных доменов
      const origins = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com', 'https://readdy.ai'];
      
      origins.forEach(origin => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
      
      // Отложенная загрузка тяжелых скриптов
      setTimeout(() => {
        // Здесь можно добавить динамическую загрузку тяжелых библиотек
        // Например: аналитика, чаты, виджеты и т.д.
      }, 1000);
    };

    // Оптимизация FCP (First Contentful Paint) и LCP (Largest Contentful Paint)
    const optimizePaints = () => {
      // Добавление requestIdleCallback для запуска низкоприоритетных операций
      const runWhenIdle = (callback) => {
        if ('requestIdleCallback' in window) {
          return window.requestIdleCallback(callback);
        } else {
          return setTimeout(callback, 50);
        }
      };
      
      // Отложенная загрузка неприоритетных изображений
      runWhenIdle(() => {
        const lazyImages = document.querySelectorAll('img[loading="lazy"]');
        if ('IntersectionObserver' in window) {
          const imageObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const image = entry.target;
                image.src = image.dataset.src;
                imageObserver.unobserve(image);
              }
            });
          });
          
          lazyImages.forEach(img => {
            if (img.dataset.src) {
              imageObserver.observe(img);
            }
          });
        }
      });
    };
    
    // Очистка ненужных слушателей событий и таймеров
    const optimizeMemoryUsage = () => {
      // Очистка неиспользуемых элементов и слушателей при переходе между страницами
      window.addEventListener('beforeunload', () => {
        // Отмена всех активных анимаций
        const animations = document.getAnimations ? document.getAnimations() : [];
        animations.forEach(animation => animation.cancel());
      });
    };

    // Запуск всех оптимизаций
    deferNonCriticalResources();
    optimizePaints();
    optimizeMemoryUsage();
    
    // Если доступен Performance API, собираем метрики
    if ('performance' in window && 'getEntriesByType' in window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = window.performance.getEntriesByType('navigation')[0];
          const paintMetrics = window.performance.getEntriesByType('paint');
          
          console.log('⚡ Performance metrics ⚡');
          console.log(`DOM Content Loaded: ${Math.round(perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart)} ms`);
          console.log(`DOM Complete: ${Math.round(perfData.domComplete)} ms`);
          console.log(`Page Load: ${Math.round(perfData.loadEventEnd - perfData.navigationStart)} ms`);
          
          paintMetrics.forEach(metric => {
            console.log(`${metric.name}: ${Math.round(metric.startTime)} ms`);
          });
        }, 0);
      });
    }
    
    return () => {
      // Очистка при размонтировании компонента
      window.removeEventListener('load', () => {});
      window.removeEventListener('beforeunload', () => {});
    };
  }, []);

  // Компонент не отображает никаких UI-элементов
  return null;
};

export default PerformanceOptimizer; 